import { EventType, MatrixClient } from 'matrix-js-sdk/src/matrix';

import { MeetingResponse } from 'api/meedioBackend/types';
import { ExtendedSpaceChildEventContent } from 'components/BreakoutRooms/useCreateBreakoutRooms/utils';
import { RoomType } from 'components/BreakoutRooms/useRoomType';
import logger from 'utils/logger';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { applyMessengerRestrictedJoin } from 'utils/messengerMeetingJoinRules';

/**
 * Sends the three Matrix events required to fully link a scheduled meeting room to a Messenger
 * room. Call this after updateMeeting({ messenger_room_id }) succeeds.
 *
 * 1. MESSENGER_CALL_STATE state event on the meeting room — call lifecycle, sidebar, hangup
 * 2. m.space.child state event on the meeting space       — room hierarchy detection
 * 3. SCHEDULED_MEETING timeline event on the messenger room — meeting card in the chat
 *
 * All three steps are fire-and-forget: failures are logged but do not throw.
 */
export const postLinkMessengerRoom = async (
  matrixClient: MatrixClient,
  matrixRoomId: string,
  messengerRoomId: string,
  meeting: MeetingResponse,
  meetingTitle: string
): Promise<void> => {
  try {
    await matrixClient.sendStateEvent(
      matrixRoomId,
      CustomMeedioMatrixEvents.MESSENGER_CALL_STATE,
      { messenger_room_id: messengerRoomId },
      ''
    );
  } catch (error) {
    logger.warn('postLinkMessengerRoom: MESSENGER_CALL_STATE failed', { error });
  }

  try {
    const childContent: ExtendedSpaceChildEventContent = { roomType: RoomType.CHAT };
    await matrixClient.sendStateEvent(matrixRoomId, EventType.SpaceChild, childContent, messengerRoomId);
  } catch (error) {
    logger.warn('postLinkMessengerRoom: m.space.child failed', { error });
  }

  try {
    const memberMxids =
      matrixClient
        .getRoom(messengerRoomId)
        ?.getJoinedMembers()
        .map((m) => m.userId) ?? [];
    await matrixClient.sendEvent(messengerRoomId, CustomMeedioMatrixEvents.SCHEDULED_MEETING, {
      meeting_id: meeting.id,
      matrix_room_id: matrixRoomId,
      scheduled_at: meeting.scheduled_at,
      ends_at: meeting.ends_at,
      title: meetingTitle,
      recurrence_rule: meeting.recurrence_rule ?? null,
      members: memberMxids,
      status: 'created',
    });
  } catch (error) {
    logger.warn('postLinkMessengerRoom: SCHEDULED_MEETING sendEvent failed', { error });
  }

  try {
    await applyMessengerRestrictedJoin(matrixClient, matrixRoomId, messengerRoomId);
  } catch (error) {
    logger.warn('postLinkMessengerRoom: applyMessengerRestrictedJoin failed', { error });
  }
};
